import React, { useState } from "react";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { useQuery } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { dummyAbsentiesmData, AbsentiesmData } from "../charts/chart";
import { formatDecimalNum } from "../../utils/url-id";
import moment from "moment";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { apiGetAbsentiesm } from "../../api/dashboard";

const periods = ["monthly", "yearly"] as const;

const DashboardAbsentChart: React.FC = () => {
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");

  const [period, setPeriod] = useState<(typeof periods)[number]>("monthly");

  const { data, isLoading, isError } = useQuery<AbsentiesmData, Error>({
    queryKey: ["absenteeism", period],
    queryFn: () => apiGetAbsentiesm(period),
    initialData: dummyAbsentiesmData, // Treated as real data, won't refetch immediately
  });

  const stats = data ?? dummyAbsentiesmData;
  console.log(stats);
  const series = [
    {
      name: "Absent Rate",
      data: stats.absentiesm.map((rate) => formatDecimalNum(rate.totalAbsent)),
    },
  ];

  const categories =
    stats.absentiesm.map((rate) => {
      if (rate.year && rate.month) {
        return moment(`${rate.year}-${rate.month}`, "YYYY-M").format(
          "MMM YYYY",
        );
      }
      return "No Data";
    }) || [];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    tooltip: {
      y: { formatter: (val) => `${Math.round(val)}` },
    },
    yaxis: {
      labels: { formatter: (val) => `${Math.round(val)}` },
    },
    xaxis: { categories },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: isSmall ? "10%" : isMedium ? "10%" : "10%",
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 1,
      colors: ["transparent"],
    },
    grid: {
      row: { colors: ["#f9fafb", "transparent"], opacity: 0.5 },
      borderColor: "#E5E7EB",
      strokeDashArray: 4,
    },
    colors: [
      "#2F66F4", // dark blue
      "#F59E0B", // amber
      "#10B981", // green
      "#EF4444", // red
      "#6366F1", // indigo
    ],
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        gap: 30,
      }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          variant="subtitle2"
          color={"neutral"}
          sx={{
            fontWeight: 700,
            fontFamily: "Inter",
            letterSpacing: "3%",
            mb: 3,
            mt: 4,
            paddingX: 2,
          }}
        >
          Absenteeism
        </Typography>
      </Stack>

      {isLoading ? (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      ) : isError ? (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      ) : (
        <ApexCharts
          options={options}
          series={series}
          type="bar"
          height={isMedium ? 300 : 400}
        />
      )}
    </div>
  );
};

export default DashboardAbsentChart;
